import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  desglosarIva,
  diasParaVencer,
  formatearMoneda,
  redactarRecordatorioDominio,
  sumarAnios,
} from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import {
  EstadoDominio,
  fechaLarga,
  formatearFechaCorta,
} from "../componentes/estado-dominio";
import { FormularioDominio } from "../componentes/formulario-dominio";
import {
  AreaTexto,
  Aviso,
  Boton,
  Campo,
  Confirmacion,
  Entrada,
  Etiqueta,
  Modal,
  Tarjeta,
} from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import {
  useActualizarDominio,
  useCorreoDominios,
  useDominio,
  useEliminarDominio,
  useEnviarRecordatorio,
  useRenovarDominio,
  type DominioConDetalle,
} from "../lib/dominios";

export function DominioDetalle() {
  const { id } = useParams({ from: "/dominios/$id" });
  // Con key para que navegar entre dominios no arrastre modales abiertos.
  return <Ficha key={id} id={id} />;
}

function Ficha({ id }: { id: string }) {
  const navegar = useNavigate();
  const { data: d, isLoading, isError, error } = useDominio(id);
  const { data: correo } = useCorreoDominios();

  const [editando, setEditando] = useState(false);
  const [renovando, setRenovando] = useState(false);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error"; texto: string } | null>(null);

  const actualizar = useActualizarDominio(id);
  const eliminar = useEliminarDominio(id);
  const enviar = useEnviarRecordatorio(id);

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  if (isError || !d) {
    return (
      <div className="space-y-4">
        <Aviso>{error instanceof ErrorApi ? error.message : "No se pudo cargar el dominio"}</Aviso>
        <Link to="/dominios" className="text-sm text-marca-600 hover:underline">
          ← Volver a dominios
        </Link>
      </div>
    );
  }

  const dias = diasParaVencer(d.fechaVencimiento);
  const borrador = redactarRecordatorioDominio({
    dominio: d.nombre,
    fechaVencimiento: d.fechaVencimiento,
    diasRestantes: dias,
    costoRenovacion: d.costoRenovacion,
    moneda: d.moneda,
    autoRenovacion: d.autoRenovacion,
    firma: "Uirtus",
  });
  const mailto =
    `mailto:${encodeURIComponent(d.destinatarios.join(","))}` +
    `?subject=${encodeURIComponent(borrador.asunto)}&body=${encodeURIComponent(borrador.texto)}`;

  return (
    <div className="space-y-5">
      <Link to="/dominios" className="text-sm text-marca-600 hover:underline">
        ← Dominios
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-lg font-semibold">{d.nombre}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <EstadoDominio dias={dias} activo={d.activo} />
            {d.autoRenovacion && <Etiqueta>Auto-renueva</Etiqueta>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Boton onClick={() => setRenovando(true)}>Registrar renovación</Boton>
          <Boton variante="secundario" onClick={() => setEditando(true)}>
            Editar
          </Boton>
          <Boton
            variante="secundario"
            disabled={actualizar.isPending}
            onClick={() => actualizar.mutate({ activo: !d.activo })}
          >
            {d.activo ? "Dar de baja" : "Reactivar"}
          </Boton>
          <Boton variante="peligro" onClick={() => setConfirmarBorrado(true)}>
            Eliminar
          </Boton>
        </div>
      </header>

      {mensaje && <Aviso tono={mensaje.tono === "exito" ? "exito" : "error"}>{mensaje.texto}</Aviso>}
      {!d.activo && (
        <Aviso tono="alerta">
          Este dominio está dado de baja: no aparece en la lista principal ni genera
          recordatorios. Registrar una renovación lo reactiva.
        </Aviso>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Tarjeta titulo="Dominio">
          <dl className="space-y-1.5 text-sm">
            <Fila etiqueta="Vencimiento" valor={fechaLarga(d.fechaVencimiento)} />
            <Fila
              etiqueta="Registrado"
              valor={d.fechaRegistro ? formatearFechaCorta(d.fechaRegistro) : "—"}
            />
            <Fila
              etiqueta="Proveedor"
              valor={
                d.proveedor?.sitioWeb ? (
                  <a
                    href={d.proveedor.sitioWeb}
                    target="_blank"
                    rel="noreferrer"
                    className="text-marca-600 hover:underline dark:text-marca-500"
                  >
                    {d.proveedor.nombre} ↗
                  </a>
                ) : (
                  (d.proveedor?.nombre ?? "—")
                )
              }
            />
            <Fila etiqueta="Cuenta" valor={d.cuentaProveedor ?? "—"} />
            <Fila
              etiqueta="Renovación (sin IVA)"
              valor={d.costoRenovacion ? formatearMoneda(d.costoRenovacion, d.moneda) : "—"}
            />
            {d.costoRenovacion && Number(d.costoRenovacion) > 0 && (
              <Fila
                etiqueta="Total con IVA (16%)"
                valor={formatearMoneda(desglosarIva(d.costoRenovacion).total, d.moneda)}
              />
            )}
          </dl>
          {d.notas && (
            <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-300">
              {d.notas}
            </p>
          )}
        </Tarjeta>

        <Tarjeta titulo="Cliente">
          <div className="space-y-1 text-sm">
            <Link
              to="/clientes/$id"
              params={{ id: d.cliente.id }}
              className="font-medium text-marca-600 hover:underline dark:text-marca-500"
            >
              {d.cliente.razonSocial}
            </Link>
            <p className="font-mono text-xs text-slate-500">{d.cliente.rfc}</p>
            {d.cliente.email && <p className="text-slate-600 dark:text-slate-300">{d.cliente.email}</p>}
            {d.cliente.telefono && (
              <p className="text-slate-600 dark:text-slate-300">{d.cliente.telefono}</p>
            )}
          </div>
        </Tarjeta>
      </div>

      <Tarjeta
        titulo="Recordatorios"
        acciones={
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={d.recordatoriosActivos}
              disabled={actualizar.isPending}
              onChange={(e) => actualizar.mutate({ recordatoriosActivos: e.target.checked })}
              className="rounded border-slate-300 text-marca-600 focus:ring-marca-500"
            />
            Activos
          </label>
        }
      >
        <div className="space-y-4 text-sm">
          <dl className="space-y-1.5">
            <Fila
              etiqueta="Se avisa"
              valor={
                d.recordatoriosActivos
                  ? `${d.diasAviso.join(", ")} días antes`
                  : "No se envían avisos automáticos"
              }
            />
            <Fila
              etiqueta="Destinatarios"
              valor={
                d.destinatarios.length > 0 ? (
                  d.destinatarios.join(", ")
                ) : (
                  <span className="text-amber-600">
                    Nadie: agrega un correo al cliente o en «Enviar a» del dominio
                  </span>
                )
              }
            />
          </dl>

          <div className="flex flex-wrap gap-2">
            <Boton
              variante="secundario"
              disabled={!correo?.habilitado || d.destinatarios.length === 0 || enviar.isPending}
              title={
                !correo?.habilitado
                  ? "El servidor no tiene correo configurado (SMTP_HOST)"
                  : d.destinatarios.length === 0
                    ? "No hay destinatarios"
                    : undefined
              }
              onClick={() => setConfirmarEnvio(true)}
            >
              Enviar recordatorio ahora
            </Boton>
            <a
              href={mailto}
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Redactar correo
            </a>
          </div>
          <p className="text-xs text-slate-500">
            «Redactar correo» abre tu programa de correo con el mensaje listo, por si prefieres
            mandarlo tú o el servidor no tiene correo configurado.
          </p>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">Historial</h3>
            {d.recordatorios.length === 0 ? (
              <p className="text-slate-500">Todavía no se ha enviado ningún aviso.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.recordatorios.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <div className="truncate">{r.asunto}</div>
                      <div className="truncate text-xs text-slate-500">
                        {new Intl.DateTimeFormat("es-MX", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        }).format(new Date(r.enviadoAt))}{" "}
                        · {r.destinatarios}
                        {r.manual && " · manual"}
                      </div>
                      {r.error && <div className="text-xs text-red-600">{r.error}</div>}
                    </div>
                    <Etiqueta tono={r.estado === "ENVIADO" ? "exito" : "peligro"}>
                      {r.estado === "ENVIADO" ? "Enviado" : "Falló"}
                    </Etiqueta>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Tarjeta>

      <Modal
        titulo="Editar dominio"
        abierto={editando}
        alCerrar={() => {
          setEditando(false);
          actualizar.reset();
        }}
      >
        <FormularioDominio
          dominio={d}
          clienteInicial={{ id: d.cliente.id, rfc: d.cliente.rfc, razonSocial: d.cliente.razonSocial }}
          enviando={actualizar.isPending}
          errorServidor={actualizar.error ? actualizar.error.message : null}
          alEnviar={(datos) =>
            actualizar.mutate(datos as never, {
              onSuccess: () => {
                setEditando(false);
                actualizar.reset();
              },
            })
          }
          alCancelar={() => {
            setEditando(false);
            actualizar.reset();
          }}
        />
      </Modal>

      <ModalRenovar key={d.fechaVencimiento} dominio={d} abierto={renovando} alCerrar={() => setRenovando(false)} />

      <Confirmacion
        abierto={confirmarEnvio}
        titulo="Enviar recordatorio"
        mensaje={
          <>
            Se enviará ahora un correo a <strong>{d.destinatarios.join(", ")}</strong> avisando
            que <span className="font-mono">{d.nombre}</span> {dias < 0 ? "ya venció" : `vence el ${fechaLarga(d.fechaVencimiento)}`}.
          </>
        }
        textoConfirmar="Enviar"
        ocupado={enviar.isPending}
        alCancelar={() => setConfirmarEnvio(false)}
        alConfirmar={() =>
          enviar.mutate(undefined, {
            onSuccess: (r) => {
              setConfirmarEnvio(false);
              setMensaje({ tono: "exito", texto: `Recordatorio enviado a ${r.destinatarios.join(", ")}.` });
            },
            onError: (e) => {
              setConfirmarEnvio(false);
              setMensaje({ tono: "error", texto: e.message });
            },
          })
        }
      />

      <Confirmacion
        abierto={confirmarBorrado}
        titulo="Eliminar dominio"
        mensaje={
          <>
            Se borrará <strong className="font-mono">{d.nombre}</strong> y su historial de avisos
            de forma definitiva. Si solo dejaste de administrarlo, usa{" "}
            <em>Dar de baja</em>: conserva el historial y deja de avisar.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBorrado(false)}
        alConfirmar={() =>
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar({ to: "/dominios" }),
            onError: (e) => {
              setConfirmarBorrado(false);
              setMensaje({ tono: "error", texto: e.message });
            },
          })
        }
      />
    </div>
  );
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-slate-500">{etiqueta}</dt>
      <dd className="text-right">{valor}</dd>
    </div>
  );
}

function ModalRenovar({
  dominio,
  abierto,
  alCerrar,
}: {
  dominio: DominioConDetalle;
  abierto: boolean;
  alCerrar: () => void;
}) {
  const renovar = useRenovarDominio(dominio.id);
  const sugerida = sumarAnios(dominio.fechaVencimiento, 1);

  const [fecha, setFecha] = useState(sugerida);
  const [costo, setCosto] = useState(dominio.costoRenovacion ?? "");
  const [nota, setNota] = useState("");

  function enviar(e: FormEvent) {
    e.preventDefault();
    renovar.mutate(
      {
        fechaVencimiento: fecha,
        costoRenovacion: costo.trim() || undefined,
        nota: nota.trim() || undefined,
      },
      {
        onSuccess: () => {
          alCerrar();
          renovar.reset();
          setNota("");
        },
      },
    );
  }

  return (
    <Modal
      titulo="Registrar renovación"
      abierto={abierto}
      alCerrar={() => {
        alCerrar();
        renovar.reset();
      }}
      ancho="max-w-md"
    >
      <form onSubmit={enviar} className="space-y-4 p-5">
        {renovar.error && <Aviso>{renovar.error.message}</Aviso>}
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Úsalo cuando el dominio ya se renovó en el proveedor. Actual:{" "}
          <strong>{fechaLarga(dominio.fechaVencimiento)}</strong>. Los avisos del ciclo
          anterior se reinician solos.
        </p>
        <Campo etiqueta="Nuevo vencimiento" requerido>
          <div className="flex gap-2">
            <Entrada type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            {[1, 2, 3].map((n) => (
              <Boton
                key={n}
                type="button"
                variante="secundario"
                onClick={() => setFecha(sumarAnios(dominio.fechaVencimiento, n))}
              >
                +{n}
              </Boton>
            ))}
          </div>
        </Campo>
        <Campo
          etiqueta="Costo de la renovación (sin IVA)"
          ayuda="Opcional. Actualiza el costo registrado."
        >
          <Entrada
            value={costo}
            onChange={(e) => setCosto(e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            className="font-mono"
          />
        </Campo>
        <Campo etiqueta="Nota">
          <AreaTexto rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </Campo>
        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Boton type="button" variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={renovar.isPending}>
            {renovar.isPending ? "Guardando…" : "Registrar"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
